import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Card,
  CardContent,
  CardActions,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import React from "react";
import { useParams } from "react-router-dom";
import { API_URL, downloadFile, IMAGES_API_URL } from "../constants";
import { useMutation, gql, useQuery } from "@apollo/client";
import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";
import { QRCodeCanvas } from "@cheprasov/qrcode";
import { fetch } from "cross-fetch";
import VisualBadgeEditor from "../Components/VisualBadgeEditor";

export default function PrinterBadgePage() {
  const { eventId, role } = useParams();
  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ textTransform: "capitalize" }}
      >
        {role} Printer Badge
      </Typography>

      <Container maxWidth="xl">
        <PrinterBadgeBuilder eventId={eventId} role={role} />
      </Container>
      <Box p={4} />
    </Box>
  );
}

function PrinterBadgeBuilder({ eventId, role }) {
  const [doc, setDoc] = React.useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = React.useState(false);
  const [editorMode, setEditorMode] = React.useState("visual"); // 'visual' or 'code'
  const [localElements, setLocalElements] = React.useState([]);

  const { data: printerBadgeData, refetch } = useQuery(
    gql`
      query ($eventId: String!, $role: String!) {
        printerBadge(eventId: $eventId, role: $role)
      }
    `,
    { variables: { eventId, role } }
  );

  const [upload, { loading, error }] = useMutation(gql`
    mutation ($file: Upload!, $eventId: String!, $role: String!) {
      uploadEventRolePrinterBadge(eventId: $eventId, role: $role, file: $file)
    }
  `);

  const [deleteBadge] = useMutation(gql`
    mutation ($eventId: String!, $role: String!) {
      deleteEventRolePrinterBadge(eventId: $eventId, role: $role)
    }
  `);

  const [saveBadge] = useMutation(gql`
    mutation ($eventId: String!, $role: String!, $badge: String!) {
      savePrinterBadge(eventId: $eventId, role: $role, badge: $badge)
    }
  `);

  const handleFileUpload = ({
    target: {
      validity,
      files: [file],
    },
  }) => {
    if (validity.valid) {
      upload({
        variables: { eventId, role, file },
      }).then(() => {
        onLoadDoc();
        refetch();
      });
    }
  };

  const onLoadDoc = async () => {
    try {
      const url = API_URL + `download-printer-badge/${eventId}/${role}`;
      const existingPdfBytes = await fetch(url).then((res) =>
        res.arrayBuffer()
      );
      setDoc(await PDFDocument.load(existingPdfBytes));
    } catch (e) {
      console.log("No existing printer badge PDF found:", e);
    }
  };

  const handleSaveElements = (badge) => {
    return saveBadge({
      variables: {
        eventId,
        role,
        badge,
      },
    })
      .then((res) => {
        alert(res.data.savePrinterBadge);
        refetch();
      })
      .catch((e) => alert(JSON.stringify(e, null, 2)));
  };

  const handleExportDesign = async (design) => {
    downloadFile(
      document,
      JSON.stringify(design),
      `${eventId}_${role}_printer_badge_design.json`
    );
  };

  const handleImportDesign = async (e) => {
    const [file] = e.target.files;
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      handleSaveElements(reader.result).then(() => refetch());
    };
    reader.readAsText(file);
  };

  const handleDeleteBadge = () => {
    if (window.confirm("Are you sure you want to delete the Printer Badge?")) {
      deleteBadge({ variables: { eventId, role } }).then(() => {
        setDoc(null);
        refetch();
      });
    }
  };

  React.useEffect(() => {
    const t = setTimeout(onLoadDoc, 1000);
    return () => clearTimeout(t);
  }, [eventId, role]);

  // Initialize local elements when data loads
  React.useEffect(() => {
    if (printerBadgeData?.printerBadge) {
      try {
        const parsedElements = JSON.parse(printerBadgeData.printerBadge);
        setLocalElements(parsedElements);
      } catch (e) {
        console.error("Error parsing printer badge data:", e);
        setLocalElements([]);
      }
    } else {
      setLocalElements([]);
    }
  }, [printerBadgeData?.printerBadge]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Grid container spacing={2}>
      {/* Status Card */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Chip
                icon={<PrintIcon />}
                label="Printer Badge"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={doc ? "PDF Uploaded" : "No PDF"}
                color={doc ? "success" : "default"}
                size="small"
              />
              <Chip
                label={
                  printerBadgeData?.printerBadge !== "[]"
                    ? "Design Created"
                    : "No Design"
                }
                color={
                  printerBadgeData?.printerBadge !== "[]"
                    ? "success"
                    : "default"
                }
                size="small"
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* File Management */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            File Management
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              Upload PDF Template
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={handleFileUpload}
              />
            </Button>

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              Import Design (JSON)
              <input
                type="file"
                accept="application/json"
                hidden
                onChange={handleImportDesign}
              />
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteBadge}
              disabled={!doc}
            >
              Remove PDF
            </Button>
          </Stack>
        </Paper>
      </Grid>

      {/* Badge Builder */}
      <Grid item xs={12}>
        {!doc ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              Upload a PDF template to start designing your Printer Badge
            </Typography>
          </Paper>
        ) : (
          <Box>
            {/* Editor Mode Tabs */}
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={editorMode}
                onChange={(e, value) => setEditorMode(value)}
                variant="fullWidth"
              >
                <Tab icon={<EditIcon />} label="Visual Editor" value="visual" />
                <Tab icon={<PreviewIcon />} label="Code Editor" value="code" />
              </Tabs>
            </Paper>

            {/* Editor Content */}
            {editorMode === "visual" ? (
              <Paper sx={{ height: "80vh", overflow: "hidden" }}>
                <VisualBadgeEditor
                  elements={localElements}
                  onElementsChange={(newElements) => {
                    // Just update local state, don't save automatically
                    setLocalElements(newElements);
                  }}
                  onSave={() => {
                    handleSaveElements(JSON.stringify(localElements));
                  }}
                  canvasWidth={400}
                  canvasHeight={600}
                />
              </Paper>
            ) : (
              <PrinterBadge
                doc={doc}
                onSave={handleSaveElements}
                onExportDesign={handleExportDesign}
                badge={printerBadgeData?.printerBadge}
              />
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}

function PrinterBadge({ doc, onSave, badge, onExportDesign }) {
  const [elements, setElements] = React.useState(
    !badge ? [] : JSON.parse(badge)
  );
  const [showTemplateDialog, setShowTemplateDialog] = React.useState(false);
  const pdfRef = React.useRef(null);

  React.useEffect(() => {
    (async () => {
      if (doc) {
        pdfRef.current.src =
          (await modifyPdf(doc, elements)) + "#page=1&zoom=100";
      }
    })();
  }, [elements]);

  const addElement = (template = null) => {
    const newElement = template || {
      id: Date.now(),
      type: "text",
      value: "$First Name",
      bold: false,
      size: 16,
      color: "black",
      bound: {
        x: 10,
        y: 600,
        width: 100,
        height: 16,
      },
    };

    setElements([...elements, JSON.stringify(newElement, null, 2)]);
  };

  const getElementTemplates = () => [
    {
      name: "Name",
      element: {
        id: Date.now(),
        type: "text",
        value: "$First Name $Last Name",
        bold: true,
        size: 18,
        color: "black",
        bound: { x: 50, y: 500, width: 200, height: 20 },
      },
    },
    {
      name: "Company",
      element: {
        id: Date.now(),
        type: "text",
        value: "$Company",
        bold: false,
        size: 14,
        color: "black",
        bound: { x: 50, y: 480, width: 200, height: 16 },
      },
    },
    {
      name: "Title",
      element: {
        id: Date.now(),
        type: "text",
        value: "$Title",
        bold: false,
        size: 12,
        color: "black",
        bound: { x: 50, y: 460, width: 200, height: 14 },
      },
    },
    {
      name: "QR Code",
      element: {
        id: Date.now(),
        type: "qr",
        value: "$QR Code URL",
        size: 80,
        bound: { x: 300, y: 400, width: 80, height: 80 },
      },
    },
    {
      name: "Printer Badge",
      element: {
        id: Date.now(),
        type: "text",
        value: "PRINTER BADGE",
        bold: true,
        size: 16,
        color: "red",
        bound: { x: 50, y: 520, width: 150, height: 18 },
      },
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Design Elements
            </Typography>

            <Stack spacing={1} sx={{ maxHeight: 400, overflow: "auto" }}>
              {elements.map((el, i) => (
                <TextField
                  key={i}
                  defaultValue={el}
                  label={`Element ${i + 1}`}
                  fullWidth
                  multiline
                  maxRows={8}
                  size="small"
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      const v = e.target.value;
                      if (!v) {
                        setElements(elements.filter((_, k) => i !== k));
                      } else {
                        setElements(
                          elements.map((a, k) => {
                            try {
                              return JSON.parse(a).id === JSON.parse(v).id
                                ? v
                                : a;
                            } catch {
                              return a;
                            }
                          })
                        );
                      }
                    }
                  }}
                />
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => addElement()}
                size="small"
              >
                Add Element
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowTemplateDialog(true)}
                size="small"
              >
                Templates
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Stack spacing={1}>
              <Button
                onClick={() => onSave(JSON.stringify(elements))}
                color="success"
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Save Design
              </Button>
              <Button
                onClick={() => onExportDesign(elements)}
                color="warning"
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
              >
                Export Design
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 1 }}>
          <embed
            ref={pdfRef}
            width="100%"
            height={842}
            type="application/pdf"
          />
        </Paper>
      </Grid>

      {/* Template Dialog */}
      <Dialog
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Element Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {getElementTemplates().map((template, index) => (
              <Grid item xs={6} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">{template.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.element.type === "text"
                        ? template.element.value
                        : "QR Code"}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        addElement(template.element);
                        setShowTemplateDialog(false);
                      }}
                    >
                      Add
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

async function modifyPdf(docOriginal, elements) {
  const doc = await docOriginal.copy();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const pages = doc.getPages();
  const page = pages[0];
  const { width, height } = page.getSize();
  page.resetPosition();

  const drawText = (
    text,
    {
      size = 16,
      bold = false,
      color = "black",
      bounds = { x: 100, y: 100, width: 100, height: 20 },
    } = {}
  ) => {
    let textWidth = (bold ? fontBold : font).widthOfTextAtSize(text, size);
    if (textWidth > bounds.width) {
      size *= 0.8;
      textWidth = (bold ? fontBold : font).widthOfTextAtSize(text, size);
    }

    page.moveTo(
      bounds.x + (bounds.width - textWidth) / 2,
      bounds.y + bounds.height - size
    );

    page.drawText(text, {
      size,
      font: bold ? fontBold : font,
      color: rgb(...parseColor(color)),
    });
  };

  const parseColor = (colorString) => {
    const colors = {
      red: [1, 0, 0],
      green: [0, 1, 0],
      blue: [0, 0, 1],
      black: [0, 0, 0],
      white: [1, 1, 1],
    };

    return colors[colorString] || colors.black;
  };

  if (elements?.length) {
    for (const str of elements) {
      try {
        const el = JSON.parse(str);

        if (el.type === "text") {
          if (
            font.widthOfTextAtSize(el.value, 18) > el.bound.width &&
            el.value.indexOf("||") > -1
          ) {
            drawText(el.value.split("||")[0].trim(), {
              bold: el.bold,
              bounds: el.bound,
              size: el.size,
              color: el.color,
            });
            drawText(el.value.split("||")[1].trim(), {
              bold: el.bold,
              bounds: { ...el.bound, y: el.bound.y - el.size - el.size * 0.25 },
              size: el.size,
              color: el.color,
            });
          } else if (font.widthOfTextAtSize(el.value, 18) > el.bound.width) {
            drawText(el.value.replace("||", " "), {
              bold: el.bold,
              size: Math.floor(el.size * 0.7),
              bounds: el.bound,
              color: el.color,
            });
          } else {
            drawText(el.value.replace("||", " "), {
              bold: el.bold,
              size: el.size,
              color: el.color,
              bounds: el.bound,
            });
          }
        } else if (el.type === "qr") {
          const qr = new QRCodeCanvas(el.value);
          const qrImg = await doc.embedPng(qr.toDataUrl());
          const qrSize = el.size;
          page.drawImage(qrImg, {
            x: el.bound.x + (el.bound.width - qrSize) / 2,
            y: el.bound.y,
            width: qrSize,
            height: qrSize,
          });
        }
      } catch (e) {
        console.error("Error processing element:", e);
      }
    }
  }

  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });

  return URL.createObjectURL(blob);
}
